import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Routes, Route, Link, useNavigate, Navigate, useSearchParams, useParams, Outlet } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { useAuth } from './context/AuthContext';
import { BookmarkProvider } from './context/BookmarkContext';
import { PointsProvider, usePoints } from './context/PointsContext';
import { useTeam } from './context/TeamContext';
import { useTheme } from './context/ThemeContext';
import { fetchPublicEntries, fetchMyEntries, fetchFolders, moveEntriesToFolder, bulkDeleteEntries } from './lib/supabase';
import { exportJSON, exportCSV } from './lib/export';
import { OUTPUT_TYPES } from './constants';
import Sidebar from './components/Sidebar';
import FolderManager from './components/FolderManager';
import EntryCard from './components/EntryCard';
import DetailPanel from './components/DetailPanel';
import EntryForm from './components/EntryForm';
import MobileNav from './components/MobileNav';
import MobileFAB from './components/MobileFAB';
import AuthPage from './pages/AuthPage';
import AdminPage from './pages/AdminPage';
import SettingsPage from './pages/SettingsPage';
import PremiumPage from './pages/PremiumPage';
import LandingPage from './pages/LandingPage';
import SkillsPage from './pages/SkillsPage';
import MarketplacePage from './pages/MarketplacePage';
import CreatorPage from './pages/CreatorPage';
import AccountPage from './pages/AccountPage';
import AdminMarketplacePage from './pages/AdminMarketplacePage';
import JoinTeamPage from './pages/JoinTeamPage';
import TeamDashboard from './pages/TeamDashboard';
import TeamSettingsPage from './pages/TeamSettingsPage';
import TeamNewPage from './pages/TeamNewPage';
import CreateTeamPage from './pages/CreateTeamPage';
import UsernameSetupPage from './pages/UsernameSetupPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import LegalPage from './pages/LegalPage';
import EntryPage from './pages/EntryPage';
import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';
import OnboardingTour from './components/OnboardingTour';
import './index.css';

function RequireAuth({ children }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

// -- Theme-aware color hook ------------------------------------
function useThemeColors() {
  const { isDark } = useTheme();
  return {
    surface:  isDark ? '#1a1a1c' : '#ffffff',
    surface2: isDark ? '#232326' : '#f0eeea',
    bg:       isDark ? '#0e0e0f' : '#f5f4f0',
  };
}

// -- Mobile detection -----------------------------------------
function useIsMobile() {
  const [ok, setOk] = React.useState(
    () => typeof window !== 'undefined' ? window.innerWidth <= 1024 : false
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px)');
    const fn = e => setOk(e.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return ok;
}

// -- Swipe to dismiss bottom sheet ----------------------------
// Returns props to spread onto the drag-handle element.
// Calls onDismiss() when user drags down > threshold px.
function useSwipeToDismiss(onDismiss, threshold = 120) {
  const startY    = React.useRef(null);
  const panelRef  = React.useRef(null);
  const animFrame = React.useRef(null);

  const onTouchStart = React.useCallback((e) => {
    startY.current = e.touches[0].clientY;
    if (panelRef.current) panelRef.current.style.transition = 'none';
  }, []);

  const onTouchMove = React.useCallback((e) => {
    if (startY.current === null) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy <= 0) return; // only allow downward drag
    cancelAnimationFrame(animFrame.current);
    animFrame.current = requestAnimationFrame(() => {
      if (panelRef.current) {
        panelRef.current.style.transform = `translateY(${dy}px)`;
        panelRef.current.style.opacity   = String(Math.max(0.4, 1 - dy / 400));
      }
    });
  }, []);

  const onTouchEnd = React.useCallback((e) => {
    if (startY.current === null) return;
    const dy = e.changedTouches[0].clientY - startY.current;
    if (panelRef.current) {
      panelRef.current.style.transition = '';
      panelRef.current.style.transform  = '';
      panelRef.current.style.opacity    = '';
    }
    startY.current = null;
    if (dy > threshold) onDismiss();
  }, [onDismiss, threshold]);

  return { panelRef, dragHandleProps: { onTouchStart, onTouchMove, onTouchEnd } };
}

// -- Pull to refresh -------------------------------------------
function usePullToRefresh(onRefresh, containerRef) {
  const startY      = React.useRef(null);
  const pulling     = React.useRef(false);
  const [hint, setHint] = React.useState(false); // show "Release to refresh"

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      // Only trigger if scrolled to the very top
      if (el.scrollTop > 0) return;
      startY.current = e.touches[0].clientY;
      pulling.current = false;
    };
    const onTouchMove = (e) => {
      if (startY.current === null) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy > 60 && !pulling.current) {
        pulling.current = true;
        setHint(true);
      }
    };
    const onTouchEnd = () => {
      if (pulling.current) {
        onRefresh();
      }
      startY.current  = null;
      pulling.current = false;
      setHint(false);
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove',  onTouchMove,  { passive: true });
    el.addEventListener('touchend',   onTouchEnd,   { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove',  onTouchMove);
      el.removeEventListener('touchend',   onTouchEnd);
    };
  }, [onRefresh, containerRef]);

  return hint;
}

// -- Model filter dropdown -------------------------------------
function ModelDropdown({ models, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { surface, surface2 } = useThemeColors();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: surface2,
          border: '0.5px solid var(--border2)',
          borderRadius: 'var(--radius-sm)',
          padding: '5px 10px',
          color: value ? 'var(--text)' : 'var(--muted)',
          fontFamily: 'var(--font)',
          fontSize: 12,
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6,
          maxWidth: 200, overflow: 'hidden',
          whiteSpace: 'nowrap', textOverflow: 'ellipsis',
        }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {value || 'All models'}
        </span>
        <span style={{ fontSize: 9, flexShrink: 0 }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: 'fixed',
          marginTop: 4,
          background: surface,
          border: '0.5px solid var(--border2)',
          borderRadius: 'var(--radius)',
          zIndex: 9999,
          minWidth: 220, maxWidth: 280,
          maxHeight: 260,
          overflowY: 'auto',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          top: ref.current ? ref.current.getBoundingClientRect().bottom + 4 : 0,
          left: ref.current ? ref.current.getBoundingClientRect().left : 0,
        }}>
          <div
            onClick={() => { onChange(''); setOpen(false); }}
            style={{
              padding: '9px 14px', fontSize: 12.5,
              color: !value ? 'var(--accent)' : 'var(--muted)',
              cursor: 'pointer', borderBottom: '0.5px solid var(--border)',
              background: !value ? 'var(--accent-dim)' : surface,
            }}>
            All models
          </div>
          {models.map(m => (
            <div
              key={m}
              onClick={() => { onChange(m); setOpen(false); }}
              style={{
                padding: '9px 14px', fontSize: 12.5,
                color: value === m ? 'var(--accent)' : 'var(--text)',
                cursor: 'pointer',
                background: value === m ? 'var(--accent-dim)' : surface,
                borderBottom: '0.5px solid var(--border)',
              }}
              onMouseEnter={e => { if (value !== m) e.currentTarget.style.background = surface2; }}
              onMouseLeave={e => { if (value !== m) e.currentTarget.style.background = value === m ? 'var(--accent-dim)' : surface; }}
            >
              {m}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// -- Marketplace creator route wrapper ------------------------
function MarketplaceCreatorRoute() {
  const { username } = useParams();
  return <MarketplacePage creatorUsername={username} />;
}

// -- Skeleton loading card ------------------------------------
function SkeletonCard() {
  return (
    <div style={{
      background: 'var(--surface)',
      border: '0.5px solid var(--border)',
      borderRadius: 'var(--radius)',
      padding: '14px 16px',
      display: 'flex', gap: 14, alignItems: 'flex-start',
    }}>
      <div className="skeleton" style={{ width: 58, minWidth: 58, height: 58, borderRadius: 9, flexShrink: 0 }} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div className="skeleton" style={{ height: 13, width: '65%', borderRadius: 4 }} />
        <div className="skeleton" style={{ height: 11, width: '90%', borderRadius: 4 }} />
        <div className="skeleton" style={{ height: 11, width: '75%', borderRadius: 4 }} />
        <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
          <div className="skeleton" style={{ height: 18, width: 52, borderRadius: 4 }} />
          <div className="skeleton" style={{ height: 18, width: 72, borderRadius: 4 }} />
        </div>
      </div>
    </div>
  );
}

function FeedView() {
  const { user, isPro, isAdmin, profile } = useAuth();
  const isMobile        = useIsMobile();
  const [showFolderMgr, setShowFolderMgr] = useState(false);
  const feedRef         = React.useRef(null);
  const scrollMemory    = React.useRef({}); // tab → scroll position
  const loadEntriesRef  = React.useRef(null); // ref so pull-to-refresh can call it before useCallback init
  const { panelRef: sheetRef, dragHandleProps } = useSwipeToDismiss(
    () => closePanel(),
    120
  );
  const refreshHint = usePullToRefresh(() => loadEntriesRef.current?.(), feedRef);
  const [showTour, setShowTour]           = useState(false);
  const [lastSavedEntry, setLastSavedEntry] = useState(null);
  const [onboardingPrefill, setOnboardingPrefill] = useState(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [entries, setEntries]           = useState([]);
  const [folders, setFolders]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [activeFilter, setActiveFilter] = useState(() => {
      const folder = searchParams.get('folder');
      if (folder) return `folder:${folder}`;
      return searchParams.get('filter') || 'all';
    });
  const [search, setSearch]             = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);
  const [modelFilter, setModelFilter]   = useState('');
  const [selectedId, setSelectedId]     = useState(null);
  const [showForm, setShowForm]         = useState(false);
  // Type pre-selected by FAB quick-add (null = no pre-selection)
  const [quickAddType, setQuickAddType] = useState(null);
  const [sidebarOpen, setSidebarOpen]   = useState(false);
  const [toast, setToast]               = useState({ show: false, msg: '' });
  const [ptsToast, setPtsToast]         = useState('');
  const { refreshPoints } = usePoints();

  const [selectMode, setSelectMode]     = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting]           = useState(false);
  const [draggingId, setDraggingId]     = useState(null);
  const [checkedIds, setCheckedIds]     = useState(new Set());
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showMoveMenu, setShowMoveMenu]     = useState(false);
  const exportMenuRef = useRef(null);

  const showToast = (msg) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast(t => ({ ...t, show: false })), 2200);
  };

  useEffect(() => {
    if (user) {  // load for all users — free users get up to FREE_FOLDER_LIMIT
      fetchFolders(user.id).then(setFolders).catch(console.error);
    }
  }, [user?.id]);

  const isMineView = activeFilter === 'mine' || activeFilter.startsWith('folder:');

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      if (isMineView) {
        if (!user) { setEntries([]); setLoading(false); return; }
        const folderId = activeFilter.startsWith('folder:') ? activeFilter.slice(7) : undefined;
        const data = await fetchMyEntries(user.id, folderId);
        setEntries(data || []);
      } else {
        const typeFilter = OUTPUT_TYPES.find(t => t.id === activeFilter) ? activeFilter : undefined;
        const data = await fetchPublicEntries({ type: typeFilter, search: debouncedSearch });
        setEntries(data || []);
      }
    } catch (e) { console.error('loadEntries:', e?.message); }
    setLoading(false);
  }, [activeFilter, debouncedSearch, user, isMineView]);

  // Keep ref in sync so pull-to-refresh always calls the latest version
  React.useEffect(() => { loadEntriesRef.current = loadEntries; }, [loadEntries]);
  useEffect(() => { loadEntries(); }, [loadEntries]);

  // Redirect ?tab=skills|marketplace — extension deep links
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'skills' || tab === 'marketplace') {
      const skill    = searchParams.get('skill')    || '';
      const pack     = searchParams.get('pack')     || '';
      const category = searchParams.get('category') || '';
      const params   = new URLSearchParams();
      if (skill)    params.set('skill', skill);
      if (pack)     params.set('pack', pack);
      if (category) params.set('category', category);
      const dest = tab === 'marketplace' ? '/marketplace' : '/skills';
      const qs   = params.toString();
      navigate(dest + (qs ? '?' + qs : ''), { replace: true });
    }
  }, [searchParams]);

  // Auto-open entry from shared link: /app?entry=<id>
  useEffect(() => {
    const entryId = searchParams.get('entry');
    if (!entryId || !entries.length) return;
    const found = entries.find(e => e.id === entryId);
    if (found) {
      setSelectedId(entryId);
      setSearchParams({}, { replace: true });
    }
  }, [entries, searchParams]);

  // ── Handle ?newEntry=<type> from FAB when navigating from other pages ──
  useEffect(() => {
    const newEntryType = searchParams.get('newEntry');
    if (!newEntryType || !user) return;
    // Clean URL immediately
    setSearchParams({}, { replace: true });
    // Open form with that type pre-selected
    const typeId = newEntryType === 'full' ? null : newEntryType;
    setQuickAddType(typeId);
    setShowForm(true);
    setSelectedId(null);
  }, [searchParams, user]);

  // Sync filter from URL (e.g. MobileNav sets ?filter=mine)
  useEffect(() => {
    const f = searchParams.get('filter');
    if (f && f !== activeFilter) setActiveFilter(f);
  }, [searchParams]);

  // Auto-show tour on first login — send to marketplace first
  useEffect(() => {
    if (profile && user && profile.has_seen_onboarding === false) {
      // New users: navigate to marketplace to discover content before the tour
      const t = setTimeout(() => {
        navigate('/marketplace');
        // Small delay then show tour overlay explaining what they're seeing
        setTimeout(() => setShowTour(true), 600);
      }, 800);
      return () => clearTimeout(t);
    }
  }, [profile?.has_seen_onboarding, user?.id]);

  // Manual tour trigger via ?tour=1 (from Sidebar "Take the tour" on any page)
  useEffect(() => {
    const tourParam = searchParams.get('tour');
    if (tourParam === '1') {
      setSearchParams({}, { replace: true });
      setTimeout(() => setShowTour(true), 150);
    }
  }, [searchParams]);

  useEffect(() => { setSelectMode(false); setCheckedIds(new Set()); }, [activeFilter]);
  useEffect(() => { setModelFilter(''); }, [activeFilter]);

  const finalDisplayed = useMemo(() => {
    let list = entries;
    if (activeFilter.startsWith('tag:')) {
      const tag = activeFilter.slice(4);
      list = list.filter(e => (e.tags || []).includes(tag));
    }
    if (modelFilter) {
      list = list.filter(e => e.model === modelFilter);
    }
    if (isMineView && search) {
      const q = search.toLowerCase();
      list = list.filter(e =>
        e.title?.toLowerCase().includes(q) ||
        e.prompt?.toLowerCase().includes(q) ||
        e.model?.toLowerCase().includes(q) ||
        (e.tags || []).join(' ').toLowerCase().includes(q)
      );
    }
    return list;
  }, [entries, activeFilter, search, modelFilter, isMineView]);

  const selectedEntry = entries.find(e => e.id === selectedId) || null;
  const checkedEntries = finalDisplayed.filter(e => checkedIds.has(e.id));

  const handleSave = (entry) => {
    if (!entry._onboarding) {
      setEntries(prev => [entry, ...prev]);
      setSelectedId(entry.id);
      refreshPoints();
      const pts = entry.is_public ? 10 : 3;
      setPtsToast(`+${pts} pts`);
      setTimeout(() => setPtsToast(''), 2200);
    }
    setShowForm(false);
    setLastSavedEntry(entry);
    showToast('Entry saved ✓');
  };

  const handleDeleted = (id) => {
    setEntries(prev => prev.filter(e => e.id !== id));
    setSelectedId(null);
    setCheckedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    showToast('Entry deleted');
  };

  const handleUpdated = (updated) => {
    setEntries(prev => prev.map(e => e.id === updated.id ? updated : e));
  };

  const openDetail = (id) => {
    // Remember scroll position so we can restore it on close
    if (feedRef.current) scrollMemory.current[activeFilter] = feedRef.current.scrollTop;
    setSelectedId(id);
    setShowForm(false);
  };
  const closePanel = () => {
    setSelectedId(null);
    setShowForm(false);
    setOnboardingPrefill(null);
    setQuickAddType(null);
    // Restore scroll position after React re-renders
    requestAnimationFrame(() => {
      if (feedRef.current && scrollMemory.current[activeFilter] != null) {
        feedRef.current.scrollTop = scrollMemory.current[activeFilter];
      }
    });
  };

  // Opens the entry form — optionally with a pre-selected type (from FAB)
  const openForm = (typeId) => {
    if (!user) { navigate('/auth'); return; }
    if (showTour) {
      setOnboardingPrefill({
        title: 'My first AI prompt',
        prompt: 'Write a short, engaging LinkedIn post about the benefits of using AI tools for content creation. Keep it under 150 words, professional but approachable.',
        type: 'text',
        model: 'ChatGPT',
        isPublic: false,
      });
    } else {
      setQuickAddType(typeId && typeId !== 'full' ? typeId : null);
    }
    setShowForm(true);
    setSelectedId(null);
  };

  // FAB handler — called from MobileFAB when on /app
  const handleFABQuickAdd = (typeId) => openForm(typeId);

  const toggleCheck = (id) => {
    setCheckedIds(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      if (n.size === 0) setSelectMode(false);
      return n;
    });
    if (!selectMode) setSelectMode(true);
  };

  const selectAll = () => { setCheckedIds(new Set(finalDisplayed.map(e => e.id))); setSelectMode(true); };
  const clearSelection = () => { setCheckedIds(new Set()); setSelectMode(false); };

  const handleBulkDelete = async () => {
    if (!checkedIds.size) return;
    setDeleting(true);
    try {
      await bulkDeleteEntries([...checkedIds]);
      setEntries(prev => prev.filter(e => !checkedIds.has(e.id)));
      const count = checkedIds.size;
      clearSelection();
      setConfirmDelete(false);
      showToast(`${count} entr${count !== 1 ? 'ies' : 'y'} deleted`);
    } catch (err) {
      showToast('Error deleting entries');
    }
    setDeleting(false);
  };

  const handleExport = (format) => {
    const toExport = checkedIds.size > 0 ? checkedEntries : finalDisplayed;
    if (format === 'json') exportJSON(toExport);
    else exportCSV(toExport);
    setShowExportMenu(false);
    showToast(`Exported ${toExport.length} entries as ${format.toUpperCase()} ✓`);
  };

  const handleMove = async (folderId) => {
    if (!checkedIds.size) return;
    try {
      await moveEntriesToFolder([...checkedIds], folderId);
      await loadEntries();
      clearSelection();
      setShowMoveMenu(false);
      showToast('Entries moved ✓');
    } catch (e) { alert(e.message); }
  };

  const handleDrop = async (entryId, folderId) => {
    if (!entryId) return;
    try {
      await moveEntriesToFolder([entryId], folderId);
      setDraggingId(null);
      await loadEntries();
      showToast('Moved to folder ✓');
    } catch (e) { alert(e.message); }
  };

  const canExport = isPro || isAdmin;
  const modelOptions = [...new Set(entries.map(e => e.model).filter(Boolean))].sort();

  // Build prefill for EntryForm
  const resolvedPrefill = onboardingPrefill
    ? onboardingPrefill
    : quickAddType
    ? { type: quickAddType }
    : null;

  return (
    <div className="shell">
      <Sidebar
        onStartTour={() => setShowTour(true)}
        entries={entries}
        folders={folders}
        onFoldersChanged={setFolders}
        activeFilter={activeFilter}
        onFilter={f => { setActiveFilter(f); setSelectedId(null); setShowForm(false); }}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        draggingId={draggingId}
        onDrop={handleDrop}
      />

      <div className="main">
        <div className="topbar">
          {/* Mobile: show search icon that expands, hide hamburger */}
          {isMobile ? (
            <>
              {search ? (
                /* Expanded search — full width */
                <div className="search-wrap" style={{ flex: 1 }}>
                  <span className="search-icon">⌕</span>
                  <input
                    autoFocus
                    type="text"
                    placeholder="Search prompts, models, tags..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ fontSize: 16 }}
                  />
                  <button
                    onClick={() => setSearch('')}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--dim)', fontSize: 16, cursor: 'pointer', padding: 4 }}>
                    ×
                  </button>
                </div>
              ) : (
                /* Collapsed: logo area + search icon */
                <>
                  <span style={{ fontSize: 15, fontWeight: 500, color: 'var(--text)', letterSpacing: '-0.02em', flex: 1 }}>
                    CopyPaste<span style={{ color: 'var(--accent)' }}>Genius</span>
                  </span>
                  <button
                    onClick={() => setSearch(' ')}
                    aria-label="Search"
                    style={{ background: 'none', border: '0.5px solid var(--border2)', borderRadius: 8, padding: '7px 10px', cursor: 'pointer', color: 'var(--muted)', display: 'flex', alignItems: 'center', WebkitTapHighlightColor: 'transparent' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                  </button>
                </>
              )}
            </>
          ) : (
            /* Desktop: original layout */
            <>
              <button className="mobile-menu-btn" onClick={() => setSidebarOpen(o => !o)} aria-label="Menu">☰</button>
              <div className="search-wrap">
                <span className="search-icon">⌕</span>
                <input
                  type="text"
                  placeholder="Search prompts, models, tags..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <button className="btn-primary" onClick={() => openForm()}><span>+ New entry</span></button>
            </>
          )}
        </div>

        <div className="filters">
          <span className="filter-label">Filter</span>
          {[{ id: 'all', label: 'All' }, ...OUTPUT_TYPES].map(t => (
            <div key={t.id}
              className={`filter-pill ${activeFilter === t.id ? 'on' : ''}`}
              onClick={() => setActiveFilter(t.id)}>
              {t.label}
            </div>
          ))}
        </div>

        {/* Model filter row */}
        <div className="model-filter-bar">
          <span className="filter-label">Model</span>
          <ModelDropdown
            models={modelOptions}
            value={modelFilter}
            onChange={setModelFilter}
          />
          {modelFilter && (
            <button
              className="filter-pill on"
              style={{ fontSize: 11, padding: '3px 10px' }}
              onClick={() => setModelFilter('')}>
              {modelFilter} ×
            </button>
          )}
        </div>

        {/* ── Folder strip — mobile only, Home + Library tabs ── */}
        {isMobile && user && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 7,
            padding: '8px 14px', overflowX: 'auto',
            borderBottom: '0.5px solid var(--border)',
            background: 'var(--bg)',
            flexShrink: 0,
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
          }}
          className="folder-strip"
          >
            {/* All chip */}
            <button
              onClick={() => setActiveFilter('mine')}
              style={{
                flexShrink: 0,
                padding: '5px 13px',
                borderRadius: 20,
                fontSize: 12,
                fontFamily: 'var(--font)',
                fontWeight: activeFilter === 'mine' ? 600 : 400,
                background: activeFilter === 'mine' ? 'var(--accent)' : 'var(--surface)',
                color: activeFilter === 'mine' ? '#fff' : 'var(--muted)',
                border: `0.5px solid ${activeFilter === 'mine' ? 'var(--accent)' : 'var(--border2)'}`,
                cursor: 'pointer',
                transition: 'all 0.12s',
                WebkitTapHighlightColor: 'transparent',
              }}>
              All
            </button>

            {/* One chip per folder */}
            {folders.map(f => {
              const isActive = activeFilter === `folder:${f.id}`;
              return (
                <button
                  key={f.id}
                  onClick={() => navigate(`/skills?folder=${f.id}`)}
                  style={{
                    flexShrink: 0,
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '5px 13px',
                    borderRadius: 20,
                    fontSize: 12,
                    fontFamily: 'var(--font)',
                    fontWeight: isActive ? 600 : 400,
                    background: isActive ? `${f.color}22` : 'var(--surface)',
                    color: isActive ? f.color : 'var(--muted)',
                    border: `0.5px solid ${isActive ? f.color + '80' : 'var(--border2)'}`,
                    cursor: 'pointer',
                    transition: 'all 0.12s',
                    WebkitTapHighlightColor: 'transparent',
                  }}>
                  <span style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: f.color, flexShrink: 0,
                    boxShadow: isActive ? `0 0 6px ${f.color}` : 'none',
                  }} />
                  {f.name}
                </button>
              );
            })}

            {/* + New folder shortcut */}
            <button
              onClick={() => setShowFolderMgr(true)}
              style={{
                flexShrink: 0,
                padding: '5px 11px',
                borderRadius: 20,
                fontSize: 12,
                fontFamily: 'var(--font)',
                background: 'none',
                color: 'var(--dim)',
                border: '0.5px dashed var(--border2)',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
              }}>
              + Folder
            </button>
          </div>
        )}

        {/* Select mode action bar — floating on mobile, inline on desktop */}
        {selectMode && isMobile && (
          <div className="select-action-bar">
            <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 500, flexShrink: 0 }}>
              {checkedIds.size} selected
            </span>
            <button onClick={selectAll} style={tbBtn}>All</button>
            <button onClick={clearSelection} style={tbBtn}>Clear</button>
            {checkedIds.size > 0 && folders.length > 0 && (
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <button
                  onClick={() => setShowMoveMenu(m => !m)}
                  style={{ ...tbBtn }}>
                  📁 Move
                </button>
                {showMoveMenu && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, marginTop: 4,
                    background: 'var(--surface)', border: '0.5px solid var(--border2)',
                    borderRadius: 'var(--radius)', zIndex: 9999,
                    minWidth: 160, boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                    overflow: 'hidden',
                  }}>
                    <div
                      onClick={() => handleMove(null)}
                      style={{ padding: '10px 14px', fontSize: 13, color: 'var(--muted)', cursor: 'pointer', borderBottom: '0.5px solid var(--border)' }}>
                      ◈ Unfiled
                    </div>
                    {folders.map(f => (
                      <div key={f.id}
                        onClick={() => handleMove(f.id)}
                        style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text)', cursor: 'pointer', borderBottom: '0.5px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: f.color, flexShrink: 0 }} />
                        {f.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {checkedIds.size > 0 && (
              <button onClick={() => setConfirmDelete(true)}
                style={{ ...tbBtn, color: '#e24b4a', borderColor: 'rgba(226,75,74,0.35)', background: 'rgba(226,75,74,0.06)', flexShrink: 0 }}>
                🗑 {checkedIds.size}
              </button>
            )}
          </div>
        )}

        <div className="stats-bar">
          {selectMode && !isMobile ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 500 }}>
                {checkedIds.size} selected
              </span>
              <button onClick={selectAll} style={tbBtn}>Select all</button>
              <button onClick={clearSelection} style={tbBtn}>Clear</button>
              {checkedIds.size > 0 && (
                <button
                  onClick={() => setConfirmDelete(true)}
                  style={{ ...tbBtn, color: '#e24b4a', borderColor: 'rgba(226,75,74,0.35)', background: 'rgba(226,75,74,0.06)' }}
                >
                  🗑 Delete ({checkedIds.size})
                </button>
              )}
              {folders.length > 0 && (
                <div style={{ position: 'relative' }} ref={exportMenuRef}>
                  <button onClick={() => { setShowMoveMenu(m => !m); setShowExportMenu(false); }} style={tbBtn}>
                    📁 Move to folder ▾
                  </button>
                  {showMoveMenu && (
                    <div style={dropMenu}>
                      <div style={dropItem} onClick={() => handleMove(null)}>◈ Unfiled</div>
                      {folders.map(f => (
                        <div key={f.id} style={dropItem} onClick={() => handleMove(f.id)}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: f.color, display: 'inline-block', marginRight: 6 }} />
                          {f.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {canExport && (
                <div style={{ position: 'relative', marginLeft: 'auto' }}>
                  <button onClick={() => { setShowExportMenu(m => !m); setShowMoveMenu(false); }}
                    style={{ ...tbBtn, background: 'var(--accent-dim)', borderColor: 'var(--accent-border)', color: 'var(--accent)' }}>
                    Export {checkedIds.size > 0 ? `(${checkedIds.size})` : 'all'} ▾
                  </button>
                  {showExportMenu && (
                    <div style={dropMenu}>
                      <div style={dropItem} onClick={() => handleExport('json')}>📄 JSON</div>
                      <div style={dropItem} onClick={() => handleExport('csv')}>📊 CSV</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="stat">Entries: <strong>{finalDisplayed.length}</strong></div>
              <div className="stat">Models: <strong>{[...new Set(entries.map(e => e.model))].length}</strong></div>
              {canExport && isMineView && finalDisplayed.length > 0 && (
                <div style={{ position: 'relative', marginLeft: 'auto' }}>
                  <button onClick={() => setShowExportMenu(m => !m)}
                    style={{ ...tbBtn, background: 'var(--accent-dim)', borderColor: 'var(--accent-border)', color: 'var(--accent)', fontSize: 11 }}>
                    Export all ▾
                  </button>
                  {showExportMenu && (
                    <div style={dropMenu}>
                      <div style={dropItem} onClick={() => handleExport('json')}>📄 JSON</div>
                      <div style={dropItem} onClick={() => handleExport('csv')}>📊 CSV</div>
                    </div>
                  )}
                </div>
              )}
              {!user && (
                <div className="stat" style={{ marginLeft: 'auto' }}>
                  <span style={{ color: 'var(--accent)', fontSize: 11 }}>Sign in to view full prompts</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Feed — bottom padding so last card clears the floating nav */}
        {refreshHint && isMobile && (
          <div style={{
            textAlign: 'center', padding: '8px', fontSize: 12,
            color: 'var(--accent)', fontFamily: 'var(--font)',
            background: 'var(--accent-dim)',
            borderBottom: '0.5px solid var(--accent-border)',
            flexShrink: 0,
          }}>
            ↓ Release to refresh
          </div>
        )}
        <div ref={feedRef} className="feed" onClick={() => { setShowExportMenu(false); setShowMoveMenu(false); }}>
          {loading ? (
            <>
              {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
            </>
          ) : finalDisplayed.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">⊘</div>
              <div className="empty-title">{search ? 'No results found' : 'No entries yet'}</div>
              <div className="empty-sub">
                {search
                  ? `Nothing matches "${search}"`
                  : isMobile
                    ? 'Tap the + button below to save your first AI prompt.'
                    : 'Hit "+ New entry" to log your first AI prompt.'
                }
              </div>
              {!search && !user && (
                <a href="/auth" style={{
                  marginTop: 4, padding: '8px 20px',
                  background: 'var(--accent)', color: '#fff',
                  borderRadius: 'var(--radius-sm)', fontSize: 13,
                  textDecoration: 'none', fontWeight: 500,
                }}>Sign in to get started</a>
              )}
            </div>
          ) : (
            finalDisplayed.map(entry => (
              <EntryCard
                key={entry.id}
                entry={entry}
                selected={entry.id === selectedId}
                onClick={() => openDetail(entry.id)}
                selectMode={selectMode}
                isChecked={checkedIds.has(entry.id)}
                onCheck={toggleCheck}
                onDragStart={user ? id => setDraggingId(id) : undefined}
              />
            ))
          )}
        </div>
      </div>

      {!selectMode && (selectedEntry || showForm) && (
        <>
          {/* Backdrop for mobile bottom sheet — tap outside to dismiss */}
          {isMobile && (
            <div
              onClick={closePanel}
              style={{
                position: 'fixed', inset: 0,
                background: 'rgba(0,0,0,0.45)',
                backdropFilter: 'blur(2px)',
                WebkitBackdropFilter: 'blur(2px)',
                zIndex: 35,
              }}
            />
          )}
          {/* Swipe-to-dismiss drag zone — covers the drag handle area at top of sheet */}
          {isMobile && (
            <div
              ref={sheetRef}
              {...dragHandleProps}
              style={{
                position: 'fixed',
                left: 0, right: 0,
                bottom: 'var(--mobile-nav-clearance)',
                height: 48,
                zIndex: 45,
                touchAction: 'none',
              }}
            />
          )}
          {showForm
            ? <EntryForm
                onSave={handleSave}
                onClose={closePanel}
                folders={folders}
                prefill={resolvedPrefill}
                isOnboarding={!!onboardingPrefill && showTour}
              />
            : <DetailPanel
                entry={selectedEntry}
                onClose={closePanel}
                onDeleted={handleDeleted}
                onUpdated={handleUpdated}
                folders={folders}
              />
          }
        </>
      )}

      {/* -- Bulk delete confirmation dialog -- */}
      {confirmDelete && (
        <>
          <div
            onClick={() => !deleting && setConfirmDelete(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)', zIndex: 9990 }}
          />
          <div style={{
            position: 'fixed', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9991, width: '90%', maxWidth: 420,
            background: 'var(--surface)',
            border: '0.5px solid var(--border2)',
            borderRadius: 16, padding: '28px 24px',
            boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
            fontFamily: 'var(--font)',
          }}>
            <div style={{ fontSize: 32, marginBottom: 14, textAlign: 'center' }}>🗑️</div>
            <h2 style={{ fontSize: 17, fontWeight: 600, color: 'var(--text)', textAlign: 'center', margin: '0 0 10px', letterSpacing: '-0.01em' }}>
              Delete {checkedIds.size} {checkedIds.size === 1 ? 'entry' : 'entries'}?
            </h2>
            <p style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'center', lineHeight: 1.6, margin: '0 0 24px' }}>
              This will permanently delete {checkedIds.size === 1 ? 'this entry' : `these ${checkedIds.size} entries`}. This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                style={{
                  flex: 1, background: 'none', border: '0.5px solid var(--border2)',
                  borderRadius: 10, padding: '11px', cursor: 'pointer',
                  fontFamily: 'var(--font)', fontSize: 14, color: 'var(--muted)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={deleting}
                style={{
                  flex: 1, background: '#e24b4a', color: '#fff', border: 'none',
                  borderRadius: 10, padding: '11px', cursor: deleting ? 'wait' : 'pointer',
                  fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600,
                  opacity: deleting ? 0.7 : 1,
                  boxShadow: '0 4px 14px rgba(226,75,74,0.35)',
                  transition: 'all 0.15s',
                }}
              >
                {deleting ? 'Deleting…' : `Delete ${checkedIds.size === 1 ? 'entry' : `${checkedIds.size} entries`}`}
              </button>
            </div>
          </div>
        </>
      )}

      {showTour && (
        <OnboardingTour
          onClose={(completed) => { setShowTour(false); if (completed) { setShowForm(false); setSelectedId(null); setOnboardingPrefill(null); } }}
          onRequestNewEntry={() => {
            setOnboardingPrefill({
              title: 'My first AI prompt',
              prompt: 'Write a short, engaging LinkedIn post about the benefits of using AI tools for content creation. Keep it under 150 words, professional but approachable.',
              type: 'text',
              model: 'ChatGPT',
              isPublic: false,
            });
            setShowForm(true);
            setSelectedId(null);
          }}
          lastSavedEntry={lastSavedEntry}
          appFormOpen={showForm}
        />
      )}
      <div className={`toast ${toast.show ? 'show' : ''}`}>{toast.msg}</div>
      {ptsToast && (
        <div style={{
          position: 'fixed', bottom: 80, right: 20, zIndex: 9999,
          background: '#7f77dd', color: '#fff',
          padding: '8px 16px', borderRadius: 20,
          fontSize: 13, fontWeight: 600,
          boxShadow: '0 4px 16px rgba(127,119,221,0.4)',
          animation: 'ptsToastIn 0.3s ease',
          fontFamily: 'var(--font)',
        }}>
          {ptsToast} ✦
        </div>
      )}

      {/* MobileFAB is now global — rendered at AppInner level */}

      <style>{`@keyframes ptsToastIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }`}</style>

      {/* Folder manager — triggered from mobile folder strip + button */}
      {showFolderMgr && (
        <FolderManager
          folders={folders || []}
          onChanged={updated => setFolders(updated)}
          onClose={() => setShowFolderMgr(false)}
        />
      )}
    </div>
  );
}

const tbBtn = {
  background: 'none', border: '0.5px solid var(--border2)',
  borderRadius: 'var(--radius-sm)', padding: '4px 10px',
  fontFamily: 'var(--font)', fontSize: 11.5, color: 'var(--muted)', cursor: 'pointer',
};
const dropMenu = {
  position: 'absolute', top: '100%', right: 0, marginTop: 4,
  background: 'var(--surface)', border: '0.5px solid var(--border2)',
  borderRadius: 'var(--radius)', minWidth: 140, zIndex: 9999,
  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
  overflow: 'hidden',
};
const dropItem = {
  padding: '10px 14px', fontSize: 13, color: 'var(--text)',
  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
  transition: 'background 0.1s',
};


// -- ShellView: renders sidebar + right panel (keeps nav visible) --------------
function ShellView() {
  const { user, isPro, isAdmin, profile } = useAuth();
  const { teams, activeTeam, switchTeam } = useTeam();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [folders, setFolders] = useState([]);

  useEffect(() => {
    if (user) {
      fetchFolders(user.id).then(setFolders).catch(console.error);
    }
  }, [user?.id]);

  return (
    <div className="shell">
      <Sidebar
        onStartTour={() => { navigate('/app?tour=1'); setSidebarOpen(false); }}
        entries={[]}
        folders={folders}
        onFoldersChanged={setFolders}
        activeFilter=""
        onFilter={() => navigate('/app')}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        draggingId={null}
        onDrop={() => {}}
      />
      <div style={{
        flex: 1, minWidth: 0,
        display: 'flex', flexDirection: 'column',
        height: '100dvh', overflow: 'hidden',
        background: 'var(--bg)',
      }}>
        {/* Mobile topbar — hidden on pages with their own headers */}
        {!window.location.pathname.startsWith('/marketplace') &&
         !window.location.pathname.startsWith('/skills') &&
         window.location.pathname !== '/creator' &&
         window.location.pathname !== '/account' && (
          <div className="topbar" style={{ justifyContent: 'flex-start', gap: 12, flexShrink: 0 }}>
            <button className="mobile-menu-btn" onClick={() => setSidebarOpen(true)}>☰</button>
            <span style={{ fontSize: 14, color: 'var(--muted)', fontFamily: 'var(--font)' }}>
              {activeTeam?.name || 'Teams'}
            </span>
          </div>
        )}
        {/* Content area — block scroll container, pages flow naturally inside */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0,
          paddingBottom: 'var(--mobile-nav-clearance)' }}>
          <Outlet />
        </div>
      </div>

      {/* MobileFAB is now global — rendered at AppInner level */}
    </div>
  );
}

function AppInner() {
  const navigate = useNavigate();
  return (
    <>
      <CookieBanner />
      <Routes>
        <Route path="/"         element={<LandingPage />} />
        <Route path="/app"      element={<FeedView />} />
        <Route path="/auth"     element={<AuthPage />} />
        <Route path="/admin"    element={<RequireAuth><AdminPage /></RequireAuth>} />
        <Route path="/settings" element={<RequireAuth><SettingsPage /></RequireAuth>} />
        <Route path="/premium"  element={<RequireAuth><PremiumPage /></RequireAuth>} />
        <Route path="/join"          element={<JoinTeamPage />} />
        {/* Marketplace — public, no auth required */}
        <Route element={<ShellView />}>
          <Route path="/marketplace"                      element={<MarketplacePage />} />
          <Route path="/marketplace/creator/:username"    element={<MarketplaceCreatorRoute />} />
          <Route path="/creator"                          element={<CreatorPage />} />
          <Route path="/account"                          element={<AccountPage />} />
          <Route path="/admin/marketplace"                element={<RequireAuth><AdminMarketplacePage /></RequireAuth>} />
        </Route>

        <Route element={<RequireAuth><ShellView /></RequireAuth>}>
          <Route path="/team"          element={<TeamDashboard />} />
          <Route path="/team/settings" element={<TeamSettingsPage />} />
          <Route path="/skills"        element={<SkillsPage />} />
        </Route>
        <Route path="/team/create"     element={<RequireAuth><CreateTeamPage /></RequireAuth>} />
        <Route path="/team/new"        element={<RequireAuth><TeamNewPage /></RequireAuth>} />
        <Route path="/setup/username"   element={<RequireAuth><UsernameSetupPage /></RequireAuth>} />
        <Route path="/auth/callback"     element={<AuthCallbackPage />} />
        <Route path="/reset-password"      element={<ResetPasswordPage />} />
        <Route path="/legal/terms"          element={<LegalPage slug="terms" />} />
        <Route path="/legal/privacy"        element={<LegalPage slug="privacy" />} />
        <Route path="/legal/cookies"        element={<LegalPage slug="cookies" />} />
        <Route path="/legal/acceptable-use" element={<LegalPage slug="acceptable-use" />} />
        <Route path="/legal/dmca"           element={<LegalPage slug="dmca" />} />
        <Route path="/legal/refunds"        element={<LegalPage slug="refunds" />} />
        <Route path="/entry/:id"             element={<EntryPage />} />
        <Route path="*"         element={<Navigate to="/" replace />} />
      </Routes>

      {/* Global mobile bottom nav + FAB — JS-gated, never render on desktop */}
      <MobileNav />
      <MobileFAB onQuickAdd={(typeId) => {
        // Navigate to /app with the type — works from any page
        navigate(`/app?newEntry=${typeId}`);
      }} />
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <PointsProvider>
        <BookmarkProvider>
          <AppInner />
        </BookmarkProvider>
      </PointsProvider>
    </ErrorBoundary>
  );
}
